import React, { useState } from 'react';
import { Button } from './ui/button';
import { CheckCircle2, ClipboardCheck, RotateCcw } from 'lucide-react';
import { useContact } from '../context/ContactContext';

const QUESTIONS = [
  { id: 'authority', q: 'Do you have your own MC authority?', options: ['Yes, active authority', "Not yet, I'm getting it", 'No, I lease on'] },
  { id: 'equipment', q: 'What equipment do you run?', options: ['Dry Van', 'Reefer', 'Flatbed', 'Power Only', 'Hotshot', 'Box Truck'] },
  { id: 'fleet', q: 'How many trucks do you operate?', options: ['Just 1 (owner operator)', '2 to 5 trucks', '6+ trucks'] },
  { id: 'insurance', q: 'Do you have active commercial insurance?', options: ['Yes', 'Not yet'] },
];

export const CarrierQuiz = () => {
  const { openContactForm } = useContact();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const select = (qid, option) => {
    setAnswers((a) => ({ ...a, [qid]: option }));
    if (step < QUESTIONS.length - 1) setStep(step + 1);
    else setDone(true);
  };

  const reset = () => { setStep(0); setAnswers({}); setDone(false); };

  const progress = done ? 100 : Math.round((step / QUESTIONS.length) * 100);
  const current = QUESTIONS[step];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-orange-50" data-testid="carrier-quiz">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <ClipboardCheck className="h-4 w-4" /> 60-Second Qualification
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Are You a Fit for Flow Fleet?</h2>
          <p className="text-gray-600 mt-3">Answer a few quick questions and see how we can keep you loaded.</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100" data-testid="quiz-card">
          <div className="h-2 bg-gray-100 rounded-full mb-8 overflow-hidden">
            <div className="h-full bg-orange-600 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>

          {!done ? (
            <div data-testid={`quiz-step-${step}`}>
              <p className="text-sm text-gray-400 mb-2">Question {step + 1} of {QUESTIONS.length}</p>
              <h3 className="text-xl font-bold text-gray-900 mb-6">{current.q}</h3>
              <div className="grid gap-3">
                {current.options.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => select(current.id, opt)}
                    data-testid={`quiz-option-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                    className="text-left px-5 py-4 rounded-xl border-2 border-gray-100 hover:border-orange-400 hover:bg-orange-50 font-medium text-gray-700 transition-all"
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4" data-testid="quiz-result">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-5" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">You're a Great Fit! 🚛</h3>
              <p className="text-gray-600 mb-8">
                Based on your answers, our dispatchers can start finding you premium {answers.equipment || 'freight'} loads right away.
                Let's get you dispatched.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={openContactForm} data-testid="quiz-request-dispatcher" className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4">
                  Request a Dispatcher
                </Button>
                <Button onClick={reset} variant="outline" data-testid="quiz-restart" className="border-gray-300 text-gray-700 px-6 py-4 flex items-center gap-2">
                  <RotateCcw className="h-4 w-4" /> Start Over
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
